// Hex terrain mesher (port of vendor/oneshot-rts/terrain.ts to the worldciv grid). Pure: typed arrays
// in, geometry arrays out, no GL. Runs in the mesh worker.
//
// Every hex gets a private centre and two inner rings at its own height, and an outer ring (corners
// + edge midpoints) shared with its neighbours. Shared points are clustered by height: hexes within
// CLIFF_STEP of each other merge into one averaged vertex (rolling ground); larger steps keep
// separate vertices joined by a wall (cliff), with a bevel drop on the high side so plateaus read
// as shouldered mesas. Hill and mountain uplift is shared mostly at its lowest value, so a mountain
// rises inside its own hex, ranges still join into massifs, and river edges stay in the valley.
//
//        ring A (0.42R)  ring B (0.8R)          shared outer ring (clustered)
//              ╲   ╱                                     │
//        ┌──────●──────┐  ← fan (6 tris)                  ▼
//        │ ╲    C    ╱ │  ← A–B band (12) + B–outer (18)  ●═════●═════●  cliff wall
//        └──────●──────┘                                 ║           ║
//                                                        ●═════●═════●  low neighbour's ring
//
// Chunks are 32×32 hexes. A chunk is meshed with a one-hex apron so normals match across chunk
// seams; only the chunk's own triangles are emitted. Shared points are keyed on an integer lattice
// with wrapped x, so both sides of the east-west seam compute identical vertices.
//
// LODs: NEAR = the full scheme above; MID = centre + shared corners (6 tris per hex) with the same
// clustered corners, so seams line up; WORLD = one dual-lattice mesh through all hex centres.

import { COLS, HEX_W, ROWS, ROW_H, hexIndex, neighbour } from '../../core/grid.ts';
import { placeChunk, type Clearing, type FeatureData } from '../features/placement.ts';
import type { PhysicalGrid } from '../hexdata.ts';
import { AUX, AUX_COUNT, BIOME_COUNT, HEX_KIND, groundNoise, hexHash, type HexShapes } from './heights.ts';

export const CHUNK_SIZE = 32;
export const CHUNK_COLS = COLS / CHUNK_SIZE;
const CHUNK_ROWS = Math.ceil(ROWS / CHUNK_SIZE);
export const CHUNK_COUNT = CHUNK_COLS * CHUNK_ROWS;

export const LOD = { NEAR: 0, MID: 1, WORLD: 2 } as const;
export type Lod = (typeof LOD)[keyof typeof LOD];

/** Geometry arrays for one mesh. Vertex attributes: position, normal, biomeA/B, aux, shape (unorm8 ×4). */
export interface MeshData {
  position: Float32Array;
  normal: Float32Array;
  /** Biome weights 0..3 (grass, plains, desert, tundra). */
  biomeA: Uint8Array;
  /** Biome weights 4..7 (snow, forest, jungle, seabed). */
  biomeB: Uint8Array;
  /** (cliff wall, rock, height up a cliff wall (0 foot → 1 top), river bank), 0..255. */
  aux: Uint8Array;
  /** (peak: 0 at the outer ring → 1 at a hill or mountain top, cavity occlusion, marsh, floodplain), 0..255. */
  shape: Uint8Array;
  index: Uint16Array | Uint32Array;
  /** Bounding box: minX, minY, minZ, maxX, maxY, maxZ. */
  box: Float32Array;
  /** Props standing on this chunk (trees, peaks, reeds…), chunk LODs only. */
  features?: FeatureData;
}

const CLIFF_STEP = 0.24;

// For the cliff decision a sea floor counts as no lower than this, so ordinary coasts slope into the
// water (the shoreline lands just under sea level: a beach) and only high coasts become cliffs.
const COAST_CLIFF_FLOOR = -0.03;
const BEVEL_DROP = 0.06;
/** Radii of a hex's two private rings (share of the outer radius). */
const RING_A = 0.42;
const RING_B = 0.8;

// Share of the uplift above the lowest contributor that a shared point keeps (0 = lowest only).
const LIFT_SHARE = 0.35;

// Cavity occlusion: mean height of a vertex's mesh neighbours above it, times this gain.
const CAVITY_GAIN = 5;

/** How much of a hex's lift, crown and noise each private ring carries. */
interface RingShape {
  lift: number;
  crown: number;
  noise: number;
}
const CENTRE: RingShape = { lift: 1, crown: 1, noise: 0 };
const SHAPE_A: RingShape = { lift: 1, crown: 0.5, noise: 0.9 };
const SHAPE_B: RingShape = { lift: 1, crown: 0.1, noise: 0.8 };
// Ring B leans toward the lowest uplift around its corner: inside a range it stays at full height
// (no moat between mountain hexes); next to a plain it starts the slope down.
const RING_B_EDGE_BLEND = 0.35;
const MID_CENTRE: RingShape = { lift: 1, crown: 0.7, noise: 0 }; // MID LOD keeps most of the mound
const MOUNTAIN_JITTER = 0.85; // share of the crown that ring heights wander by

const PEAK_SHIFT = 0.28; // how far a mountain's peak wanders from the hex centre
const RIVER_DIP = 0.045;
const MAP_EDGE_DROP = 1.5;
const WORLD_CROWN = 0.6;
const WORLD_NORMAL_GAIN = 6; // exaggerate relief in world-LOD normals so ranges shade like a relief map
const BYTE = 255;

// Integer lattice for shared points: x in units of √3/4, z in units of 1/4.
const LATTICE_X = HEX_W / 4;
const LATTICE_Z = 0.25;
const LATTICE_W = COLS * 4;
const LATTICE_PAD = 8;

// Corner k and mid k offsets from a hex centre on the lattice (corner k at 60k − 30 degrees).
const CORNER_LAT: ReadonlyArray<readonly [number, number]> = [[2, -2], [2, 2], [0, 4], [-2, 2], [-2, -2], [0, -4]];
const MID_LAT: ReadonlyArray<readonly [number, number]> = [[2, 0], [1, 3], [-1, 3], [-2, 0], [-1, -3], [1, -3]];
const CORNER_DX = Array.from({ length: 6 }, (_, k) => Math.cos(((60 * k - 30) * Math.PI) / 180));
const CORNER_DZ = Array.from({ length: 6 }, (_, k) => Math.sin(((60 * k - 30) * Math.PI) / 180));

/** Per-vertex extras besides position and biome weights, each 0..255. */
interface VertexAttrs {
  cliff: number;
  rock: number;
  river: number;
  peak: number;
  marsh: number;
  flood: number;
}

// Byte offsets inside the aux and shape attributes.
const AUX_AT = { CLIFF: 0, ROCK: 1, WALL: 2, RIVER: 3 } as const;
const SHAPE_AT = { PEAK: 0, CAVITY: 1, MARSH: 2, FLOOD: 3 } as const;

class Sink {
  readonly pos: number[] = [];
  readonly nrm: number[] = [];
  readonly ba: number[] = [];
  readonly bb: number[] = [];
  readonly aux: number[] = [];
  readonly shape: number[] = [];
  count = 0;

  add(x: number, y: number, z: number, w: ArrayLike<number>, a: VertexAttrs): number {
    this.pos.push(x, y, z);
    this.nrm.push(0, 0, 0);
    this.ba.push(w[0], w[1], w[2], w[3]);
    this.bb.push(w[4], w[5], w[6], w[7]);
    this.aux.push(a.cliff, a.rock, 0, a.river);
    this.shape.push(a.peak, 0, a.marsh, a.flood);
    return this.count++;
  }

  /** Copy a vertex with a new cliff value (walls and skirts need private normals). */
  copy(id: number, cliff: number): number {
    const p = this.pos;
    const w = [...this.ba.slice(id * 4, id * 4 + 4), ...this.bb.slice(id * 4, id * 4 + 4)];
    const a = this.aux;
    const sh = this.shape;
    return this.add(p[id * 3], p[id * 3 + 1], p[id * 3 + 2], w, {
      cliff,
      rock: a[id * 4 + AUX_AT.ROCK],
      river: a[id * 4 + AUX_AT.RIVER],
      peak: sh[id * 4 + SHAPE_AT.PEAK],
      marsh: sh[id * 4 + SHAPE_AT.MARSH],
      flood: sh[id * 4 + SHAPE_AT.FLOOD],
    });
  }
}

/** One cluster vertex per contributor at a shared point. */
interface SharedPoint {
  hexes: number[];
  ids: number[];
}

interface Tris {
  idx: number[];
  /** 1 = emit, 0 = apron (normals only). */
  emit: number[];
}

function pushTri(t: Tris, a: number, b: number, c: number, emit: number): void {
  t.idx.push(a, b, c);
  t.emit.push(emit);
}

function wrapLattice(x: number): number {
  return ((x % LATTICE_W) + LATTICE_W) % LATTICE_W;
}

function wrapWorldX(x: number): number {
  return wrapLattice(x / LATTICE_X) * LATTICE_X;
}

function isWater(shapes: HexShapes, t: number): boolean {
  return shapes.kind[t] === HEX_KIND.WATER || shapes.kind[t] === HEX_KIND.DEEP;
}

/** Height used to decide cliffs between hexes (see COAST_CLIFF_FLOOR). */
function cliffBase(shapes: HexShapes, t: number): number {
  return isWater(shapes, t) ? Math.max(shapes.base[t], COAST_CLIFF_FLOOR) : shapes.base[t];
}

/** Mountain hex: solid rock (heights.ts sets the rock weight to a full byte). */
function rocky(shapes: HexShapes, t: number): boolean {
  return shapes.aux[t * AUX_COUNT + AUX.ROCK] >= BYTE;
}

/** Hexes around corner k of hex t: t, its neighbour across edge k−1 and across edge k. */
function cornerHexes(t: number, k: number): number[] {
  return [t, neighbour(t, (k + 5) % 6), neighbour(t, k)];
}

/** True when a river runs along the edge between adjacent hexes a and b (either may be -1). */
function riverBetween(river: Uint8Array, a: number, b: number): boolean {
  if (a < 0 || b < 0) {
    return false;
  }
  for (let d = 0; d < 6; d++) {
    if (neighbour(a, d) === b) {
      return (river[a] & (1 << d)) !== 0;
    }
  }
  return false;
}

/** Builds shared points for one mesh: cache by lattice key, cluster by height. */
class PointCache {
  private readonly points = new Map<number, SharedPoint>();
  private readonly w = new Float32Array(BIOME_COUNT);
  private readonly shapes: HexShapes;
  private readonly river: Uint8Array;
  private readonly sink: Sink;

  constructor(shapes: HexShapes, river: Uint8Array, sink: Sink) {
    this.shapes = shapes;
    this.river = river;
    this.sink = sink;
  }

  /** Vertex id of the cluster that `forHex` belongs to at lattice point (lx, lz). */
  vertex(lx: number, lz: number, contributors: number[], forHex: number): number {
    const key = (lz + LATTICE_PAD) * LATTICE_W + wrapLattice(lx);
    let p = this.points.get(key);
    if (!p) {
      p = this.make(lx, lz, contributors);
      this.points.set(key, p);
    }
    return p.ids[p.hexes.indexOf(forHex)];
  }

  private make(lx: number, lz: number, contributors: number[]): SharedPoint {
    const s = this.shapes;
    const x = lx * LATTICE_X;
    const z = lz * LATTICE_Z;
    const hexes = contributors.filter((h) => h >= 0).sort((a, b) => cliffBase(s, a) - cliffBase(s, b) || a - b);

    // A river on any edge between the contributors carves this point.
    let river = false;
    for (let i = 0; i < hexes.length && !river; i++) {
      for (let j = i + 1; j < hexes.length && !river; j++) {
        river = riverBetween(this.river, hexes[i], hexes[j]);
      }
    }

    const noise = groundNoise(wrapWorldX(x), z);
    const ids: number[] = [];
    let start = 0;
    let group = 0;
    for (let i = 1; i <= hexes.length; i++) {
      // A mountain hex's step down to land stays a slope: a wall there is a sandstone plank lying on
      // the range. Only a drop to water (a sea cliff) still gets a wall.
      const rockSlope = i < hexes.length && rocky(s, hexes[i]) && !isWater(s, hexes[i - 1]);
      const split = i === hexes.length || (!rockSlope && cliffBase(s, hexes[i]) - cliffBase(s, hexes[i - 1]) >= CLIFF_STEP);
      if (!split) {
        continue;
      }
      const id = this.emitGroup(hexes.slice(start, i), x, z, noise, { bevel: group > 0, river });
      for (let j = start; j < i; j++) {
        ids.push(id);
      }
      start = i;
      group++;
    }
    return { hexes, ids };
  }

  /** One vertex for a cluster: `bevel` drops a higher cluster's rim, `river` carves the point. */
  private emitGroup(members: number[], x: number, z: number, noise: number, opts: { bevel: boolean; river: boolean }): number {
    const { bevel, river } = opts;
    const s = this.shapes;
    const w = this.w;
    w.fill(0);
    const aux = new Array<number>(AUX_COUNT).fill(0);
    let base = 0;
    let lift = 0;
    let liftMin = Infinity;
    let amp = 0;
    for (const m of members) {
      base += s.base[m];
      lift += s.lift[m];
      liftMin = Math.min(liftMin, s.lift[m]);
      amp += s.amp[m];
      for (let c = 0; c < AUX_COUNT; c++) {
        aux[c] += s.aux[m * AUX_COUNT + c];
      }
      for (let b = 0; b < BIOME_COUNT; b++) {
        w[b] += s.weights[m * BIOME_COUNT + b];
      }
    }
    const n = members.length;
    for (let b = 0; b < BIOME_COUNT; b++) {
      w[b] /= n;
    }

    // Uplift: mostly the lowest contributor's, so a lone mountain rises inside its own hex; a
    // river point takes the lowest only, keeping the channel at the valley floor.
    const share = river ? 0 : LIFT_SHARE;
    const shared = liftMin + share * (lift / n - liftMin);
    const h = base / n + shared + (noise * amp) / n - (bevel ? BEVEL_DROP : 0) - (river ? RIVER_DIP : 0);
    return this.sink.add(x, h, z, w, {
      cliff: 0,
      rock: aux[AUX.ROCK] / n,
      river: river ? BYTE : 0,
      peak: 0,
      marsh: aux[AUX.MARSH] / n,
      flood: aux[AUX.FLOOD] / n,
    });
  }
}

/** Hash in [0, 1) of a world position (private vertex jitter; seams never share these). */
function jitter(x: number, z: number): number {
  let h = Math.imul(Math.round(x * 64) | 0, 0x27d4eb2d) ^ Math.imul(Math.round(z * 64) | 0, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  return ((h ^ (h >>> 13)) >>> 0) / 4294967296;
}

/** Chunk key ↔ chunk column/row. */
export function chunkKey(cc: number, rc: number): number {
  return rc * CHUNK_COLS + cc;
}

export function chunkCR(key: number): { cc: number; rc: number } {
  return { cc: key % CHUNK_COLS, rc: Math.floor(key / CHUNK_COLS) };
}

/** Build one chunk at LOD NEAR or MID, with the props standing on it when `grid` is given. */
export function buildChunk(shapes: HexShapes, river: Uint8Array, key: number, lod: Lod, grid?: PhysicalGrid, clearings?: readonly Clearing[]): MeshData {
  const { cc, rc } = chunkCR(key);
  const c0 = cc * CHUNK_SIZE;
  const c1 = c0 + CHUNK_SIZE; // exclusive
  const r0 = rc * CHUNK_SIZE;
  const r1 = Math.min(ROWS, r0 + CHUNK_SIZE);

  const sink = new Sink();
  const cache = new PointCache(shapes, river, sink);
  const tris: Tris = { idx: [], emit: [] };
  const near = lod === LOD.NEAR;

  // Outer vertex ids per owned hex, for walls and skirts after normals are known, and the range of
  // its surface triangles (props sample the ground there).
  const owned: Array<{ t: number; cx: number; cz: number; corners: number[]; mids: number[]; tris: [number, number] }> = [];

  for (let r = Math.max(0, r0 - 1); r < Math.min(ROWS, r1 + 1); r++) {
    for (let c = c0 - 1; c < c1 + 1; c++) {
      const t = hexIndex(c, r);
      const isOwned = c >= c0 && c < c1 && r >= r0 && r < r1 ? 1 : 0;
      const lx = 4 * c + 2 + 2 * (r & 1);
      const lz = 6 * r + 3;
      const cx = lx * LATTICE_X;
      const cz = lz * LATTICE_Z;

      const corners: number[] = [];
      const mids: number[] = [];
      for (let k = 0; k < 6; k++) {
        corners.push(cache.vertex(lx + CORNER_LAT[k][0], lz + CORNER_LAT[k][1], cornerHexes(t, k), t));
        if (near) {
          mids.push(cache.vertex(lx + MID_LAT[k][0], lz + MID_LAT[k][1], [t, neighbour(t, k)], t));
        }
      }
      // Open ocean is left to the water plane; its points still shape the shelf edge.
      const emit = isOwned && shapes.kind[t] !== HEX_KIND.DEEP ? 1 : 0;
      const first = tris.emit.length;
      if (near) {
        emitNearHex(shapes, sink, tris, t, cx, cz, corners, mids, emit);
      } else {
        emitMidHex(shapes, sink, tris, t, cx, cz, corners, emit);
      }
      if (isOwned) {
        owned.push({ t, cx, cz, corners, mids, tris: [first, tris.emit.length] });
      }
    }
  }

  if (near) {
    straightenRim(sink, shapes, owned, c0, c1, r0, r1);
  }
  accumulateNormals(sink, tris.idx);
  accumulateCavity(sink, tris.idx);

  // Walls between clusters, and skirts at the map edge. Chunk rims need no skirt: corners are
  // shared bit for bit and NEAR rims are straightened to the MID edge (straightenRim).
  for (const o of owned) {
    if (shapes.kind[o.t] === HEX_KIND.DEEP) {
      continue;
    }
    for (let k = 0; k < 6; k++) {
      const k1 = (k + 1) % 6;
      const n = neighbour(o.t, k);
      const top = near ? [o.corners[k], o.mids[k], o.corners[k1]] : [o.corners[k], o.corners[k1]];
      if (n < 0) {
        emitSkirt(sink, tris, top, MAP_EDGE_DROP);
        continue;
      }
      if (shapes.base[o.t] > shapes.base[n]) {
        const low = lowSide(cache, o, k, n, lod);
        const same = low.every((id, i) => id === top[i]);
        if (!same) {
          emitWall(sink, tris, top, low, o.cx, o.cz);
        }
      }
    }
  }

  const mesh = extract(sink, tris);
  if (grid) {
    const ranges = new Map(owned.map((o) => [o.t, o.tris] as const));
    const ground = (t: number, x: number, z: number): number => groundIn(sink, tris.idx, ranges.get(t), x, z);
    mesh.features = placeChunk({ grid, shapes, detail: near ? 'near' : 'mid', ground, hexes: owned, c0, r0, clearings });
  }
  return mesh;
}

/**
 * Ground height at (x, z) on a hex's surface triangles (XZ barycentric). Points outside every
 * triangle (past the rim) take the nearest vertex's height.
 */
function groundIn(sink: Sink, idx: number[], range: readonly [number, number] | undefined, x: number, z: number): number {
  if (!range) {
    return 0;
  }
  const p = sink.pos;
  let nearest = Infinity;
  let nearestY = 0;
  for (let tri = range[0]; tri < range[1]; tri++) {
    const a = idx[tri * 3] * 3;
    const b = idx[tri * 3 + 1] * 3;
    const c = idx[tri * 3 + 2] * 3;
    const v0x = p[b] - p[a];
    const v0z = p[b + 2] - p[a + 2];
    const v1x = p[c] - p[a];
    const v1z = p[c + 2] - p[a + 2];
    const v2x = x - p[a];
    const v2z = z - p[a + 2];
    const det = v0x * v1z - v1x * v0z;
    if (Math.abs(det) > 1e-9) {
      const u = (v2x * v1z - v1x * v2z) / det;
      const v = (v0x * v2z - v2x * v0z) / det;
      if (u >= -1e-4 && v >= -1e-4 && u + v <= 1 + 1e-4) {
        return p[a + 1] + u * (p[b + 1] - p[a + 1]) + v * (p[c + 1] - p[a + 1]);
      }
    }
    const d = v2x * v2x + v2z * v2z;
    if (d < nearest) {
      nearest = d;
      nearestY = p[a + 1];
    }
  }
  return nearestY;
}

/**
 * NEAR rims match MID: on an edge between this chunk and the next, the edge-midpoint vertex lies
 * on the straight line between its two corners, which is the edge a MID chunk draws. Both chunks
 * compute it from the same shared corners, so NEAR–NEAR and NEAR–MID seams close exactly.
 */
function straightenRim(sink: Sink, shapes: HexShapes, owned: ReadonlyArray<{ t: number; corners: number[]; mids: number[] }>, c0: number, c1: number, r0: number, r1: number): void {
  const p = sink.pos;
  for (const o of owned) {
    if (shapes.kind[o.t] === HEX_KIND.DEEP) {
      continue;
    }
    for (let k = 0; k < 6; k++) {
      const n = neighbour(o.t, k);
      if (n < 0 || inChunk(n, c0, c1, r0, r1)) {
        continue;
      }
      const a = o.corners[k];
      const b = o.corners[(k + 1) % 6];
      p[o.mids[k] * 3 + 1] = (p[a * 3 + 1] + p[b * 3 + 1]) / 2;
    }
  }
}

function inChunk(n: number, c0: number, c1: number, r0: number, r1: number): boolean {
  const nc = n % COLS;
  const nr = Math.floor(n / COLS);
  return nc >= c0 && nc < c1 && nr >= r0 && nr < r1;
}

/** The low neighbour's vertex ids along edge k of the high hex. */
function lowSide(cache: PointCache, o: { t: number; cx: number; cz: number }, k: number, n: number, lod: Lod): number[] {
  const k1 = (k + 1) % 6;
  const lx = Math.round(o.cx / LATTICE_X);
  const lz = Math.round(o.cz / LATTICE_Z);
  const c0 = cache.vertex(lx + CORNER_LAT[k][0], lz + CORNER_LAT[k][1], cornerHexes(o.t, k), n);
  const c1 = cache.vertex(lx + CORNER_LAT[k1][0], lz + CORNER_LAT[k1][1], cornerHexes(o.t, k1), n);
  if (lod !== LOD.NEAR) {
    return [c0, c1];
  }
  const m = cache.vertex(lx + MID_LAT[k][0], lz + MID_LAT[k][1], [o.t, n], n);
  return [c0, m, c1];
}

/** Share of a hex's crown the ring profile keeps at radius r (0 centre → 1 outer ring). */
export function crownProfile(r: number): number {
  if (r < RING_A) {
    return CENTRE.crown + ((SHAPE_A.crown - CENTRE.crown) * r) / RING_A;
  }
  if (r < RING_B) {
    return SHAPE_A.crown + ((SHAPE_B.crown - SHAPE_A.crown) * (r - RING_A)) / (RING_B - RING_A);
  }
  return SHAPE_B.crown * Math.max(0, 1 - (r - RING_B) / (1 - RING_B));
}

function privateVertex(shapes: HexShapes, sink: Sink, t: number, x: number, z: number, ring: RingShape, lift = shapes.lift[t] * ring.lift): number {
  const w = shapes.weights.subarray(t * BIOME_COUNT, t * BIOME_COUNT + BIOME_COUNT);
  const rock = shapes.aux[t * AUX_COUNT + AUX.ROCK];
  let crown = shapes.crown[t] * ring.crown * shapes.crownScale[t];

  // Mountains: each hex gets its own peak height (crownScale), and ring heights wander, so ranges
  // do not read as a regular lattice of mounds.
  if (rock === BYTE && ring.crown < 1) {
    crown *= 1 + (jitter(x, z) - 0.5) * 2 * MOUNTAIN_JITTER;
  }
  const y = shapes.base[t] + lift + crown + groundNoise(wrapWorldX(x), z) * shapes.amp[t] * ring.noise;
  return sink.add(x, y, z, w, {
    cliff: 0,
    rock,
    river: 0,
    peak: Math.round(ring.crown * BYTE),
    marsh: shapes.aux[t * AUX_COUNT + AUX.MARSH],
    flood: shapes.aux[t * AUX_COUNT + AUX.FLOOD],
  });
}

function emitNearHex(shapes: HexShapes, sink: Sink, tris: Tris, t: number, cx: number, cz: number, corners: number[], mids: number[], emit: number): void {
  // A mountain's peak sits off-centre by a per-hex random offset.
  const mountain = shapes.aux[t * AUX_COUNT + AUX.ROCK] === BYTE;
  const px = mountain ? (hexHash(t, 1) - 0.5) * 2 * PEAK_SHIFT : 0;
  const pz = mountain ? (hexHash(t, 2) - 0.5) * 2 * PEAK_SHIFT : 0;
  const centre = privateVertex(shapes, sink, t, cx + px, cz + pz, CENTRE);
  const a: number[] = [];
  const b: number[] = [];
  for (let k = 0; k < 6; k++) {
    a.push(privateVertex(shapes, sink, t, cx + px * 0.6 + RING_A * CORNER_DX[k], cz + pz * 0.6 + RING_A * CORNER_DZ[k], SHAPE_A));
  }
  for (let k = 0; k < 6; k++) {
    const lowest = Math.min(...cornerHexes(t, k).map((h) => (h < 0 ? shapes.lift[t] : shapes.lift[h])));
    const lift = shapes.lift[t] + (lowest - shapes.lift[t]) * RING_B_EDGE_BLEND;
    b.push(privateVertex(shapes, sink, t, cx + RING_B * CORNER_DX[k], cz + RING_B * CORNER_DZ[k], SHAPE_B, lift));
  }
  for (let k = 0; k < 6; k++) {
    const k1 = (k + 1) % 6;
    pushTri(tris, centre, a[k1], a[k], emit);
    pushTri(tris, a[k], b[k1], b[k], emit);
    pushTri(tris, a[k], a[k1], b[k1], emit);
    pushTri(tris, b[k], mids[k], corners[k], emit);
    pushTri(tris, b[k], b[k1], mids[k], emit);
    pushTri(tris, b[k1], corners[k1], mids[k], emit);
  }
}

function emitMidHex(shapes: HexShapes, sink: Sink, tris: Tris, t: number, cx: number, cz: number, corners: number[], emit: number): void {
  const centre = privateVertex(shapes, sink, t, cx, cz, MID_CENTRE);
  for (let k = 0; k < 6; k++) {
    pushTri(tris, centre, corners[(k + 1) % 6], corners[k], emit);
  }
}

function accumulateNormals(sink: Sink, idx: number[]): void {
  const p = sink.pos;
  const n = sink.nrm;
  for (let i = 0; i < idx.length; i += 3) {
    const a = idx[i] * 3;
    const b = idx[i + 1] * 3;
    const c = idx[i + 2] * 3;
    const abx = p[b] - p[a];
    const aby = p[b + 1] - p[a + 1];
    const abz = p[b + 2] - p[a + 2];
    const acx = p[c] - p[a];
    const acy = p[c + 1] - p[a + 1];
    const acz = p[c + 2] - p[a + 2];
    const nx = aby * acz - abz * acy;
    const ny = abz * acx - abx * acz;
    const nz = abx * acy - aby * acx;
    for (const v of [a, b, c]) {
      n[v] += nx;
      n[v + 1] += ny;
      n[v + 2] += nz;
    }
  }
  normalise(n);
}

function normalise(n: number[]): void {
  for (let v = 0; v < n.length; v += 3) {
    const len = Math.hypot(n[v], n[v + 1], n[v + 2]);
    if (len > 0) {
      n[v] /= len;
      n[v + 1] /= len;
      n[v + 2] /= len;
    } else {
      n[v + 1] = 1;
    }
  }
}

/**
 * Cavity occlusion per vertex: how far its mesh neighbours rise above it on average. Valleys,
 * river channels and the feet of mountains darken; ridges and flat ground stay clear. Apron
 * triangles are included, so chunk seams agree.
 */
function accumulateCavity(sink: Sink, idx: number[]): void {
  const p = sink.pos;
  const sum = new Float32Array(sink.count);
  const n = new Uint16Array(sink.count);
  for (let i = 0; i < idx.length; i += 3) {
    for (let j = 0; j < 3; j++) {
      const v = idx[i + j];
      const a = idx[i + ((j + 1) % 3)];
      const b = idx[i + ((j + 2) % 3)];
      sum[v] += p[a * 3 + 1] + p[b * 3 + 1] - 2 * p[v * 3 + 1];
      n[v] += 2;
    }
  }
  for (let v = 0; v < sink.count; v++) {
    if (n[v] === 0) {
      continue;
    }
    const occ = Math.min(1, Math.max(0, (sum[v] / n[v]) * CAVITY_GAIN));
    sink.shape[v * 4 + SHAPE_AT.CAVITY] = Math.round(occ * BYTE);
  }
}

/** Push a triangle, flipping it if its face normal points against `want` (x, z outward). */
function pushOriented(sink: Sink, tris: Tris, a: number, b: number, c: number, wx: number, wz: number): void {
  const p = sink.pos;
  const abx = p[b * 3] - p[a * 3];
  const abz = p[b * 3 + 2] - p[a * 3 + 2];
  const aby = p[b * 3 + 1] - p[a * 3 + 1];
  const acx = p[c * 3] - p[a * 3];
  const acy = p[c * 3 + 1] - p[a * 3 + 1];
  const acz = p[c * 3 + 2] - p[a * 3 + 2];
  const nx = aby * acz - abz * acy;
  const nz = abx * acy - aby * acx;
  if (nx * wx + nz * wz >= 0) {
    pushTri(tris, a, b, c, 1);
  } else {
    pushTri(tris, a, c, b, 1);
  }
}

/** Cliff wall between a high outer edge and the low neighbour's, facing away from the hex centre. */
function emitWall(sink: Sink, tris: Tris, topIds: number[], lowIds: number[], cx: number, cz: number): void {
  const p = sink.pos;
  const mid = topIds[Math.floor(topIds.length / 2)];
  const midLow = lowIds[Math.floor(lowIds.length / 2)];
  const mx = (p[topIds[0] * 3] + p[topIds[topIds.length - 1] * 3]) / 2 - cx;
  const mz = (p[topIds[0] * 3 + 2] + p[topIds[topIds.length - 1] * 3 + 2]) / 2 - cz;
  const len = Math.hypot(mx, mz) || 1;
  const dy = Math.max(p[mid * 3 + 1] - p[midLow * 3 + 1], 1e-3);
  const tilt = Math.atan2(0.05, dy); // near-vertical walls lean slightly outward
  const nx = (mx / len) * Math.cos(tilt);
  const nz = (mz / len) * Math.cos(tilt);
  const ny = Math.sin(tilt);

  const top = topIds.map((id) => sink.copy(id, BYTE));
  const low = lowIds.map((id) => sink.copy(id, BYTE));
  // aux.z runs 0 at the wall's foot → 1 at its top (the material darkens the foot).
  for (const id of top) {
    sink.aux[id * 4 + AUX_AT.WALL] = BYTE;
  }
  for (const id of [...top, ...low]) {
    sink.nrm[id * 3] = nx;
    sink.nrm[id * 3 + 1] = ny;
    sink.nrm[id * 3 + 2] = nz;
  }
  for (let s = 0; s + 1 < top.length; s++) {
    pushOriented(sink, tris, top[s], top[s + 1], low[s + 1], mx, mz);
    pushOriented(sink, tris, top[s], low[s + 1], low[s], mx, mz);
  }
}

/** Vertical skirt hanging `drop` below an outer edge, shaded like the surface above it. */
function emitSkirt(sink: Sink, tris: Tris, topIds: number[], drop: number): void {
  const p = sink.pos;
  const low = topIds.map((id) => {
    const c = sink.copy(id, 0);
    p[c * 3 + 1] -= drop;
    sink.nrm[c * 3] = sink.nrm[id * 3];
    sink.nrm[c * 3 + 1] = sink.nrm[id * 3 + 1];
    sink.nrm[c * 3 + 2] = sink.nrm[id * 3 + 2];
    return c;
  });
  // Outward = perpendicular to the edge in XZ, pointing away from the surface side: unknown here,
  // so emit both windings (skirts are tiny and rarely seen).
  for (let s = 0; s + 1 < topIds.length; s++) {
    pushTri(tris, topIds[s], topIds[s + 1], low[s + 1], 1);
    pushTri(tris, topIds[s], low[s + 1], low[s], 1);
    pushTri(tris, topIds[s], low[s + 1], topIds[s + 1], 1);
    pushTri(tris, topIds[s], low[s], low[s + 1], 1);
  }
}

/** Keep emitted triangles only and compact the vertex arrays. */
function extract(sink: Sink, tris: Tris): MeshData {
  const remap = new Int32Array(sink.count).fill(-1);
  const out: number[] = [];
  let n = 0;
  for (let i = 0; i < tris.emit.length; i++) {
    if (tris.emit[i] === 0) {
      continue;
    }
    for (let j = 0; j < 3; j++) {
      const v = tris.idx[i * 3 + j];
      if (remap[v] < 0) {
        remap[v] = n++;
      }
      out.push(remap[v]);
    }
  }

  const position = new Float32Array(n * 3);
  const normal = new Float32Array(n * 3);
  const biomeA = new Uint8Array(n * 4);
  const biomeB = new Uint8Array(n * 4);
  const aux = new Uint8Array(n * 4);
  const shape = new Uint8Array(n * 4);
  const box = new Float32Array([Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity]);
  for (let v = 0; v < sink.count; v++) {
    const at = remap[v];
    if (at < 0) {
      continue;
    }
    for (let c = 0; c < 3; c++) {
      const value = sink.pos[v * 3 + c];
      position[at * 3 + c] = value;
      normal[at * 3 + c] = sink.nrm[v * 3 + c];
      box[c] = Math.min(box[c], value);
      box[c + 3] = Math.max(box[c + 3], value);
    }
    for (let c = 0; c < 4; c++) {
      biomeA[at * 4 + c] = sink.ba[v * 4 + c];
      biomeB[at * 4 + c] = sink.bb[v * 4 + c];
      aux[at * 4 + c] = sink.aux[v * 4 + c];
      shape[at * 4 + c] = sink.shape[v * 4 + c];
    }
  }
  const index = n < 65536 ? new Uint16Array(out) : new Uint32Array(out);
  return { position, normal, biomeA, biomeB, aux, shape, index, box };
}

/** One mesh through every hex centre (dual lattice) for the world zoom band. */
export function buildWorld(shapes: HexShapes): MeshData {
  const perRow = COLS + 1; // last column repeats column 0 one world-width east
  const count = perRow * ROWS;
  const position = new Float32Array(count * 3);
  const normal = new Float32Array(count * 3);
  const biomeA = new Uint8Array(count * 4);
  const biomeB = new Uint8Array(count * 4);
  const aux = new Uint8Array(count * 4);
  const shape = new Uint8Array(count * 4);
  const lift = new Float32Array(count);

  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c <= COLS; c++) {
      const v = r * perRow + c;
      const t = hexIndex(c, r);
      const y = shapes.base[t] + shapes.lift[t] + shapes.crown[t] * WORLD_CROWN;
      position[v * 3] = HEX_W * (c + 0.5 + 0.5 * (r & 1));
      position[v * 3 + 1] = y;
      position[v * 3 + 2] = ROW_H * (r + 0.5);
      lift[v] = y * WORLD_NORMAL_GAIN;
      for (let b = 0; b < 4; b++) {
        biomeA[v * 4 + b] = shapes.weights[t * BIOME_COUNT + b];
        biomeB[v * 4 + b] = shapes.weights[t * BIOME_COUNT + 4 + b];
      }
      aux[v * 4 + AUX_AT.ROCK] = shapes.aux[t * AUX_COUNT + AUX.ROCK];
      shape[v * 4 + SHAPE_AT.PEAK] = shapes.crown[t] > 0 ? Math.round(WORLD_CROWN * BYTE) : 0;
      shape[v * 4 + SHAPE_AT.MARSH] = shapes.aux[t * AUX_COUNT + AUX.MARSH];
      shape[v * 4 + SHAPE_AT.FLOOD] = shapes.aux[t * AUX_COUNT + AUX.FLOOD];
    }
  }

  const idx: number[] = [];
  const deep = (v: number): boolean => shapes.kind[hexIndex(v % perRow, Math.floor(v / perRow))] === HEX_KIND.DEEP;
  const tri = (a: number, b: number, c: number): void => {
    if (deep(a) && deep(b) && deep(c)) {
      return;
    }
    // Orient up (+y): cross(b − a, c − a).y > 0.
    const abx = position[b * 3] - position[a * 3];
    const abz = position[b * 3 + 2] - position[a * 3 + 2];
    const acx = position[c * 3] - position[a * 3];
    const acz = position[c * 3 + 2] - position[a * 3 + 2];
    if (abz * acx - abx * acz > 0) {
      idx.push(a, b, c);
    } else {
      idx.push(a, c, b);
    }
  };
  for (let r = 0; r + 1 < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const a = r * perRow + c;
      const b = (r + 1) * perRow + c;
      if ((r & 1) === 0) {
        tri(a, b, a + 1);
        tri(a + 1, b, b + 1);
      } else {
        tri(a, b + 1, b);
        tri(a, a + 1, b + 1);
      }
    }
  }

  // Normals from exaggerated heights: world zoom reads like a shaded relief map.
  for (let i = 0; i < idx.length; i += 3) {
    const [a, b, c] = [idx[i], idx[i + 1], idx[i + 2]];
    const abx = position[b * 3] - position[a * 3];
    const aby = lift[b] - lift[a];
    const abz = position[b * 3 + 2] - position[a * 3 + 2];
    const acx = position[c * 3] - position[a * 3];
    const acy = lift[c] - lift[a];
    const acz = position[c * 3 + 2] - position[a * 3 + 2];
    const nx = aby * acz - abz * acy;
    const ny = abz * acx - abx * acz;
    const nz = abx * acy - aby * acx;
    for (const v of [a, b, c]) {
      normal[v * 3] += nx;
      normal[v * 3 + 1] += ny;
      normal[v * 3 + 2] += nz;
    }
  }
  for (let v = 0; v < count; v++) {
    const len = Math.hypot(normal[v * 3], normal[v * 3 + 1], normal[v * 3 + 2]) || 1;
    normal[v * 3] /= len;
    normal[v * 3 + 1] = normal[v * 3 + 1] / len || 1;
    normal[v * 3 + 2] /= len;
  }

  const box = new Float32Array([0, -1, 0, HEX_W * (COLS + 1), 1.5, ROW_H * ROWS]);
  return { position, normal, biomeA, biomeB, aux, shape, index: new Uint32Array(idx), box };
}
